import React, { useEffect, useId, useRef, useState } from "react";
import { ScaleLoader } from "react-spinners";
import AccessibleDialog from "../../components/UI/Modals/AccessibleDialog/AccessibleDialog";
import { writeConsultReport } from "../../services/onlineConsultService";
import {
  OnlineConsultResponse,
  OptionalReportSection,
} from "../../types/api";
import { formatUtcTime } from "./formatUtcTime";
import styles from "./ReportModal.module.scss";

// ITAM-2598: write-once pharmacist report for a COMPLETED consult. A successful
// POST stores the report, generates the PDF and queues the email to the patient
// — there is no edit or delete afterwards, hence the persistent warning.

const TITLE_ID = "consult-report-title";

// Backend has no length validation and the DB columns cap at ~65k BYTES
// (diacritics are 2 bytes in UTF-8) — clamp well under it, visibly.
export const REPORT_MAX_LENGTH = 10000;
const OPTIONAL_FIELD_MAX_LENGTH = 2000;

type ReportFieldKey = OptionalReportSection | "report";

// ITAM-2610: field order mirrors the PDF layout — three context sections above
// the savet text, two follow-up sections below it. Only savet is required.
// Labels use the same typography as the PDF section labels (backend
// messages_sr.properties), minus the trailing colons.
const FIELDS: {
  key: ReportFieldKey;
  label: string;
  rows: number;
  maxLength: number;
}[] = [
  {
    key: "allergies",
    label: "Alergije",
    rows: 2,
    maxLength: OPTIONAL_FIELD_MAX_LENGTH,
  },
  {
    key: "currentTherapy",
    label: "Trenutna (akutna) / hronična terapija / suplementacija",
    rows: 2,
    maxLength: OPTIONAL_FIELD_MAX_LENGTH,
  },
  {
    key: "contactReason",
    label: "Razlog obraćanja / povod za savetovanje",
    rows: 2,
    maxLength: OPTIONAL_FIELD_MAX_LENGTH,
  },
  { key: "report", label: "Savet", rows: 6, maxLength: REPORT_MAX_LENGTH },
  { key: "note", label: "Napomena", rows: 2, maxLength: OPTIONAL_FIELD_MAX_LENGTH },
  {
    key: "followUpPlan",
    label: "Plan praćenja / naredni koraci",
    rows: 2,
    maxLength: OPTIONAL_FIELD_MAX_LENGTH,
  },
];

const EMPTY_FIELDS: Record<ReportFieldKey, string> = {
  allergies: "",
  currentTherapy: "",
  contactReason: "",
  report: "",
  note: "",
  followUpPlan: "",
};

// The backend's 400 bodies are raw English strings (duplicate report /
// not-COMPLETED) and the blank-validation 400 is a JSON map — neither is
// showable in this Serbian UI, so messages map by HTTP status alone.
const reportErrorMessage = (error: unknown): string => {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status === 400) {
    return "Slanje nije uspelo — izveštaj možda već postoji ili konsultacija nije završena.";
  }
  if (status === 404) {
    return "Konsultacija nije pronađena.";
  }
  return "Greška pri slanju izveštaja. Pokušajte ponovo.";
};

export interface ReportModalProps {
  consult: OnlineConsultResponse;
  /**
   * refetch=true when the list may be stale: after a successful send, or after
   * a 400/404 (a report may already exist / the consult changed server-side).
   */
  onClose: (refetch: boolean) => void;
}

type Phase = "EDITING" | "SUBMITTING" | "SUCCESS";

const ReportModal: React.FC<ReportModalProps> = ({ consult, onClose }) => {
  const [phase, setPhase] = useState<Phase>("EDITING");
  const [fields, setFields] = useState(EMPTY_FIELDS);
  const [error, setError] = useState<string | null>(null);
  const [hadConflict, setHadConflict] = useState(false);

  const firstTextareaRef = useRef<HTMLTextAreaElement>(null);
  const reportTextareaRef = useRef<HTMLTextAreaElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const reactId = useId();
  const warningId = `${reactId}-report-warning`;

  const isSubmitting = phase === "SUBMITTING";
  const isSuccess = phase === "SUCCESS";
  const reportTrimmed = fields.report.trim();
  const hasDraft = Object.values(fields).some((value) => value.trim() !== "");

  const setField = (key: ReportFieldKey, value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  // EDITING -> SUCCESS happens with the modal already open, so AccessibleDialog's
  // initialFocusRef can't help — move focus to the close button by hand, or
  // keyboard/screen-reader users land on <body> when the submit button unmounts.
  useEffect(() => {
    if (isSuccess) {
      closeRef.current?.focus();
    }
  }, [isSuccess]);

  // Same failure mode on the error path: SUBMITTING disables the focused
  // submit button (focus falls to <body>, outside react-modal's Tab trap), so
  // bring focus back to the savet textarea — the field that necessarily holds
  // a draft, since submit is gated on it — when the failure lands.
  useEffect(() => {
    if (error && phase === "EDITING") {
      reportTextareaRef.current?.focus();
    }
  }, [error, phase]);

  const handleRequestClose = () => {
    if (isSubmitting) {
      return;
    }
    onClose(isSuccess || hadConflict);
  };

  const handleSubmit = async () => {
    if (!reportTrimmed || isSubmitting) {
      return;
    }
    setPhase("SUBMITTING");
    setError(null);
    try {
      await writeConsultReport(consult.id, fields);
      setPhase("SUCCESS");
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 400 || status === 404) {
        setHadConflict(true);
      }
      setError(reportErrorMessage(err));
      setPhase("EDITING");
    }
  };

  const patientName =
    consult.callerInfo.joinName ?? consult.callerInfo.userEmail;

  return (
    <AccessibleDialog
      isOpen={true}
      onRequestClose={handleRequestClose}
      titleId={TITLE_ID}
      initialFocusRef={firstTextareaRef}
      maxWidth={560}
      // A misclick on the overlay must not discard a written draft; Escape and
      // the explicit close affordances still work.
      shouldCloseOnOverlayClick={!hasDraft || isSuccess}
      className={styles.dialogInner}
    >
      <div className={styles.container}>
        <header className={styles.header}>
          <h2 id={TITLE_ID} className={styles.title}>
            Izveštaj farmaceuta
          </h2>
          <p className={styles.subtitle}>
            {patientName} — konsultacija od {formatUtcTime(consult.createdTime)}
          </p>
        </header>

        {isSuccess ? (
          <div className={styles.successBody} role="status">
            <p className={styles.successHeadline}>Izveštaj je sačuvan.</p>
            <p className={styles.successNote}>
              Pacijentu će biti poslat na email kao PDF dokument.
            </p>
            <div className={styles.footerActions}>
              <button
                type="button"
                ref={closeRef}
                className={styles.primary}
                onClick={() => onClose(true)}
              >
                Zatvori
              </button>
            </div>
          </div>
        ) : (
          <>
            {error && (
              <div className={styles.errorBanner} role="alert">
                <span className={styles.errorText}>{error}</span>
                <button
                  type="button"
                  className={styles.errorDismiss}
                  onClick={() => setError(null)}
                  aria-label="Zatvori poruku o grešci"
                >
                  Zatvori
                </button>
              </div>
            )}

            <div className={styles.body}>
              {FIELDS.map((field, index) => {
                const fieldId = `${reactId}-${field.key}`;
                const isReport = field.key === "report";
                return (
                  <div key={field.key} className={styles.field}>
                    <label className={styles.label} htmlFor={fieldId}>
                      {field.label}
                      {!isReport && (
                        <span className={styles.optionalTag}> (opciono)</span>
                      )}
                    </label>
                    <textarea
                      id={fieldId}
                      ref={
                        isReport
                          ? reportTextareaRef
                          : index === 0
                          ? firstTextareaRef
                          : undefined
                      }
                      className={styles.textarea}
                      rows={field.rows}
                      maxLength={field.maxLength}
                      value={fields[field.key]}
                      onChange={(e) => setField(field.key, e.target.value)}
                      disabled={isSubmitting}
                      // The warning must be part of the dialog's entry
                      // announcement (initial focus sits on the first field),
                      // and of the savet field it belongs to.
                      aria-describedby={
                        isReport || index === 0 ? warningId : undefined
                      }
                    />
                    <div className={styles.fieldMeta}>
                      {isReport ? (
                        <span id={warningId} className={styles.warning}>
                          Izveštaj se nakon slanja ne može menjati i biće
                          poslat pacijentu na email.
                        </span>
                      ) : (
                        <span />
                      )}
                      <span className={styles.counter} aria-hidden="true">
                        {fields[field.key].length.toLocaleString("sr-RS")} /{" "}
                        {field.maxLength.toLocaleString("sr-RS")}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            <footer className={styles.footer}>
              <button
                type="button"
                className={styles.secondary}
                onClick={handleRequestClose}
                disabled={isSubmitting}
              >
                Otkaži
              </button>
              <button
                type="button"
                className={styles.primary}
                onClick={handleSubmit}
                disabled={!reportTrimmed || isSubmitting}
              >
                {isSubmitting ? (
                  <span className={styles.submitSpinner}>
                    <ScaleLoader color="#ffffff" height={14} width={3} />
                  </span>
                ) : (
                  "Pošalji izveštaj"
                )}
              </button>
            </footer>
          </>
        )}
      </div>
    </AccessibleDialog>
  );
};

export default ReportModal;
